import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Icon from '../components/atoms/Icon';
import InterviewContinuationBanner from '../components/InterviewContinuationBanner';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

const PlacementTrainingPage = () => {
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [companyTypeFilter, setCompanyTypeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [showCompanyTypeDropdown, setShowCompanyTypeDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const session = SessionManager.getCurrentSession();
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;

  // Scroll handler for sticky sidebar
  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = 200;
      setIsSticky(window.scrollY > heroHeight);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const headers = {};
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      
      const response = await fetch(`${apiUrl}/placement-training/companies`, { headers });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
        setFilteredCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    let filtered = companies;

    if (searchQuery) {
      filtered = filtered.filter(company =>
        company.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (company.description && company.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (positionFilter) {
      filtered = filtered.filter(company =>
        company.total_positions > 0
      );
    }

    if (companyTypeFilter) {
      filtered = filtered.filter(company =>
        company.company_type === companyTypeFilter
      );
    }

    if (locationFilter) {
      filtered = filtered.filter(company =>
        company.headquarters && company.headquarters.toLowerCase().includes(locationFilter.toLowerCase())
      );
    }

    setFilteredCompanies(filtered);
  };

  useEffect(() => {
    handleFilterChange();
  }, [searchQuery, positionFilter, companyTypeFilter, locationFilter]);

  const companyTypes = ['IT Services', 'Product', 'Consulting', 'BPO'];
  const popularLocations = ['Bangalore', 'Hyderabad', 'Mumbai', 'Delhi NCR', 'Pune', 'Chennai', 'Remote'];

  const resetFilters = () => {
    setSearchQuery('');
    setPositionFilter('');
    setCompanyTypeFilter('');
    setLocationFilter('');
  };

  const activeFiltersCount = 
    (searchQuery ? 1 : 0) +
    (positionFilter ? 1 : 0) +
    (companyTypeFilter ? 1 : 0) +
    (locationFilter ? 1 : 0);

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-gray-50">
      <InterviewContinuationBanner />
      
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <LandingHeader />
      </div>
      
      {/* Hero Banner */}
      <div className="relative bg-[#0a1929] overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&h=400&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1929] via-[#0a1929]/95 to-[#0a1929]/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Aptirix Placement Training
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-4">
              Practice with real company assessments and get certified
            </p>
            <p className="text-base text-gray-400 leading-relaxed max-w-3xl mx-auto">
              Master your placement interviews with AI-powered mock assessments from top companies. 
              Practice aptitude tests, coding challenges, technical interviews, and HR rounds. 
              Get instant feedback, track your progress, and earn certificates to boost your career prospects.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex">
        {/* Left Sidebar - Sticky */}
        <aside 
          ref={sidebarRef}
          className={`w-64 bg-white border-r border-gray-200 ${
            isSticky ? 'fixed top-16 h-[calc(100vh-4rem)] overflow-y-auto' : 'relative'
          }`}
          style={{ zIndex: 30 }}
        >
          <div className="p-6 space-y-6">
            {/* Dashboard Button for logged in candidates, Be Partner for non-logged in users */}
            {session?.role === 'candidate' ? (
              <button
                onClick={() => navigate('/candidate/dashboard')}
                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-md hover:shadow-lg"
              >
                <Icon name="dashboard" size={20} />
                <span>My Dashboard</span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/placement-training-landing')}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-md hover:shadow-lg"
              >
                <Icon name="handshake" size={20} />
                <span>Be Partner</span>
              </button>
            )}

            {/* Search */}
            <div>
              <div className="relative">
                <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search Companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-3 rounded-lg border border-gray-300 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none"
                />
              </div>
            </div>

            {/* Company Type Dropdown */}
            <div>
              <button
                onClick={() => {
                  setShowCompanyTypeDropdown(!showCompanyTypeDropdown);
                  setShowLocationDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-medium text-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="business" size={18} />
                  <span>Company Type</span>
                </div>
                <div className="flex items-center space-x-2">
                  {companyTypeFilter && (
                    <span className="bg-teal-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      1
                    </span>
                  )}
                  <Icon name={showCompanyTypeDropdown ? "expand_less" : "expand_more"} size={18} />
                </div>
              </button>
              
              {showCompanyTypeDropdown && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1">
                  {companyTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        setCompanyTypeFilter(companyTypeFilter === type ? '' : type);
                      }}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                        companyTypeFilter === type
                          ? 'bg-teal-600 text-white'
                          : 'hover:bg-white text-gray-700'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Location Dropdown */}
            <div>
              <button
                onClick={() => {
                  setShowLocationDropdown(!showLocationDropdown);
                  setShowCompanyTypeDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-medium text-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name="location_on" size={18} />
                  <span>Location</span>
                </div>
                <div className="flex items-center space-x-2">
                  {locationFilter && (
                    <span className="bg-teal-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      1
                    </span>
                  )}
                  <Icon name={showLocationDropdown ? "expand_less" : "expand_more"} size={18} />
                </div>
              </button>
              
              {showLocationDropdown && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1">
                  {popularLocations.map((loc) => (
                    <button
                      key={loc}
                      onClick={() => {
                        setLocationFilter(locationFilter === loc ? '' : loc);
                      }}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                        locationFilter === loc
                          ? 'bg-teal-600 text-white'
                          : 'hover:bg-white text-gray-700'
                      }`}
                    >
                      {loc}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear Filters */}
            {activeFiltersCount > 0 && (
              <button
                onClick={resetFilters}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors"
              >
                <Icon name="close" size={18} />
                <span>Clear all ({activeFiltersCount})</span>
              </button>
            )}
          </div>
        </aside>

        {/* Main Companies Listing */}
        <main className={`flex-1 ${isSticky ? 'ml-64' : ''}`}>
          <div className="p-6">
            {/* Results Header */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                {searchQuery ? `Results for "${searchQuery}"` : 'All Companies'}
              </h2>
              <p className="text-sm text-gray-600">
                Showing {filteredCompanies.length} of {companies.length} companies
              </p>
            </div>

            {/* Companies Grid */}
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading companies...</p>
                </div>
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
                <Icon name="search_off" size={64} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No companies found
                </h3>
                <p className="text-gray-600 mb-6">
                  Try adjusting your filters or search criteria
                </p>
                <button
                  onClick={resetFilters}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCompanies.map((company) => (
                  <div
                    key={company.id}
                    onClick={() => navigate(`/placement-training/company/${company.id}`)}
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg hover:border-teal-300 transition-all cursor-pointer group"
                  >
                    {/* Company Logo */}
                    <div className="flex items-center justify-center h-24 mb-4 bg-gray-50 rounded-lg group-hover:bg-teal-50 transition-colors">
                      {company.company_logo_url ? (
                        <img
                          src={company.company_logo_url}
                          alt={company.company_name}
                          className="max-h-20 max-w-full object-contain"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-xl flex items-center justify-center">
                          <span className="text-2xl font-bold text-white">
                            {company.company_name.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Company Name */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2 text-center group-hover:text-teal-600 transition-colors">
                      {company.company_name}
                    </h3>

                    {/* Company Type Badge */}
                    {company.company_type && (
                      <div className="flex justify-center mb-3">
                        <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                          {company.company_type}
                        </span>
                      </div>
                    )}

                    {/* Company Info */}
                    <div className="space-y-2 mb-4">
                      {company.headquarters && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Icon name="location_on" size={16} />
                          <span>{company.headquarters}</span>
                        </div>
                      )}
                      {company.employee_count && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Icon name="people" size={16} />
                          <span>{company.employee_count} Employees</span>
                        </div>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900">{company.total_positions || 0}</div>
                        <div className="text-xs text-gray-500">Positions</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900">{company.total_assessments || 0}</div>
                        <div className="text-xs text-gray-500">Attempts</div>
                      </div>
                    </div>

                    {/* Featured Badge */}
                    {company.is_featured && (
                      <div className="mt-3 flex items-center justify-center gap-1 text-yellow-600">
                        <Icon name="star" size={16} />
                        <span className="text-xs font-medium">Featured</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PlacementTrainingPage;
